import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import {
  ColumnAggregation,
  DatasetColumn,
  DatasetConfig,
  DatasetJoin
} from '../select-datasource-modal/select-datasource-modal.model';

type DatasourceAction = 'existing' | 'create';

@Component({
  selector: 'app-inbuilt-datasource',
  standalone: false,
  templateUrl: './inbuilt-datasource.component.html',
  styleUrl: './inbuilt-datasource.component.scss'
})
export class InbuiltDatasourceComponent {
  @Output() selectDataSource = new EventEmitter<any>();

  @ViewChild('selectDatasourceModal') private selectDatasourceModal: any;
  @ViewChild('dsBody') private dsBody: any;
  @Input() hasDataSource: boolean = false;
  @Input() reportNameFromQueryParams: string = '';
  @Input() hasReportAdminRole: boolean = false;
  @Input() initialDatasets: DatasetConfig[] | null = null;

  selectedDatasourceAction: DatasourceAction | null = null;

  open(): void {
    this.openDatasourceModal(false);
  }

  onDatasourceAction(action: DatasourceAction): void {
    this.selectedDatasourceAction = null;
    this.openDatasourceModal(action === 'existing');
  }

  private openDatasourceModal(useExistingDatasets: boolean): void {
    this.dsBody?.resetDatasets(useExistingDatasets);
    this.selectDatasourceModal?.show();
    this.dsBody?.ensureDatasourceOptionsLoaded();
  }

  onModalApply(result: { payload: any; response?: any }): void {
    this.selectDatasourceModal?.hide();
    this.selectDataSource.emit(result.payload);
  }

  onModalCancelled(): void {
    this.selectDatasourceModal?.hide();
  }

  @Input()
  set dataSourceJson(value: any) {
    this.initialDatasets = this.mapExistingDatasets(value);
  }

  private mapExistingDatasets(dataSourceJson: any): DatasetConfig[] | null {
    if (!Array.isArray(dataSourceJson?.datasets)) {
      return null;
    }

    return dataSourceJson.datasets.map((dataset: any, datasetIndex: number) => {
      const datasetId = String(dataset?.id || `existing-dataset-${datasetIndex + 1}`);
      const tableName = String(dataset?.table_name || '');
      const columns: DatasetColumn[] = (Array.isArray(dataset?.columns) ? dataset.columns : [])
        .map((column: any): DatasetColumn | null => {
          const key = String(column?.key ?? column?.name ?? '');
          if (!key) {
            return null;
          }
          const type: DatasetColumn['type'] = column?.type === 'Number' ? 'Number' : 'Text';
          const aggregation: ColumnAggregation =
            column?.aggregation === 'sum' || column?.aggregation === 'average' ||
            column?.aggregation === 'none' || column?.aggregation === 'disabled'
              ? column.aggregation
              : type === 'Number' ? 'none' : 'disabled';
          return {
            key,
            name: String(column?.name ?? key),
            alias: String(column?.alias ?? ''),
            Formatted_Column: column?.Formatted_Column ?? null,
            type,
            description: String(column?.description ?? ''),
            data_type: String(column?.data_type ?? type),
            DataType_Category: column?.DataType_Category ?? null,
            is_primary_key: !!column?.is_primary_key,
            is_foreign_key: !!column?.is_foreign_key,
            is_restricted: !!column?.is_restricted,
            restriction_condition: column?.restriction_condition ?? null,
            sample_values: String(column?.sample_values ?? ''),
            synonyms: column?.synonyms ?? null,
            constraints_info: column?.constraints_info ?? null,
            business_rules: column?.business_rules ?? null,
            filterColumn: column?.filterColumn && typeof column.filterColumn === 'object' ? column.filterColumn : null,
            uniqueColumnsForJoin: Array.isArray(column?.uniqueColumnsForJoin)
              ? column.uniqueColumnsForJoin.map((value: any) => String(value))
              : null,
            Alias_Query: column?.Alias_Query ?? null,
            dataset_name: String(column?.dataset_name ?? ''),
            table_name: String(column?.table_name ?? tableName),
            selected: column?.selected !== false,
            aggregation
          };
        })
        .filter((column: DatasetColumn | null): column is DatasetColumn => column !== null);
      const rawJoins = Array.isArray(dataset?.joins) ? dataset.joins : [];
      const joins: DatasetJoin[] = rawJoins.map((join: any, joinIndex: number) => ({
        id: `${datasetId}-join-${joinIndex + 1}`,
        targetDatasetId: join?.targetDatasetId == null ? null : String(join.targetDatasetId),
        joinType: join?.joinType ?? null,
        columnMappings: (Array.isArray(join?.columnMappings) ? join.columnMappings : [])
          .map((mapping: any, mappingIndex: number) => ({
            id: `${datasetId}-join-${joinIndex + 1}-mapping-${mappingIndex + 1}`,
            leftColumn: mapping?.leftColumn == null ? null : String(mapping.leftColumn),
            rightColumn: mapping?.rightColumn == null ? null : String(mapping.rightColumn)
          }))
      }));

      return {
        id: datasetId,
        name: String(dataset?.name ?? ''),
        dataset_name: String(dataset?.dataset_name ?? ''),
        table_name: tableName,
        table_description: String(dataset?.table_description ?? ''),
        Alias_Query: dataset?.Alias_Query ?? null,
        business_rules: dataset?.business_rules ?? null,
        is_restricted: !!dataset?.is_restricted,
        restriction_condition: dataset?.restriction_condition ?? null,
        isPrimary: typeof dataset?.isPrimary === 'boolean' ? dataset.isPrimary : datasetIndex === 0,
        manageColumnsOpen: false,
        columnsLoading: false,
        columnsLoaded: !tableName,
        columns,
        relationshipsOpen: joins.length > 0,
        joins
      };
    });
  }
}
